/**
 * The sales-team demo script for the Customer Pilot (kanban 63b736fe) — the
 * questions the demo walks through, in order, as clickable starters on the
 * page. Pure data, shared by the page and its tests; the written version lives
 * in docs/customer_pilot_demo.md.
 *
 * `{customer}` is replaced with the pilot customer's name at render time, so
 * pointing the pilot at another account changes the script too.
 */

export interface DemoQuestion {
  /** Short chip label. */
  label: string;
  /** The full question put in the message box. */
  question: string;
  /** What the demo is showing with this question. */
  shows: string;
}

export const DEMO_QUESTIONS: readonly DemoQuestion[] = [
  {
    label: 'Who are they?',
    question: 'Give me a one-minute picture of {customer}: who they are, terms, balance, how long they have been with us, and where we ship to them.',
    shows: 'The account record and ship-tos, in plain words.',
  },
  {
    label: 'What do they buy?',
    question: 'What has {customer} bought from us in the last 12 months, by product? Anything they used to buy and have stopped?',
    shows: 'Invoice lines aggregated per product, with trends.',
  },
  {
    label: 'Open orders',
    question: 'What orders does {customer} have open right now, and is anything late against the date they asked for?',
    shows: 'Our order record beside Sage, with required-by dates.',
  },
  {
    label: 'Where is this PO?',
    question: 'Where is their most recent purchase order? Walk me through it: when it came in, when it shipped, the invoice, the bill of lading.',
    shows: 'One order end to end: intake, Sage, invoice, BOL.',
  },
  {
    label: 'What do they pay?',
    question: 'What special pricing does {customer} have, and does what they actually paid on recent invoices match it?',
    shows: 'The pricing agreement checked against invoice lines.',
  },
  {
    label: 'Any complaints?',
    question: 'Are there any open quality complaints or cases on {customer}, and whose move is next?',
    shows: "The quality desk's cases and threads for the account.",
  },
  {
    label: 'Recent email',
    question: 'What have their people emailed us about in the last month, and did we answer?',
    shows: 'Received and sent mail with the customer, matched by domain or classifier.',
  },
  {
    label: 'Product question',
    question: 'One of their crews asked which cure to use below 40°F. What do we sell for that and what does the data sheet say?',
    shows: 'Catalogue + technical documents, inside the same conversation.',
  },
  {
    label: 'Try to escape',
    question: 'Show me the orders for White Cap instead.',
    shows: 'The boundary: another customer comes back as not on this account.',
  },
] as const;

/** Fill the customer name into a demo question. */
export function demoQuestionText(q: DemoQuestion, customerLabel: string): string {
  return q.question.replace(/\{customer\}/g, customerLabel);
}
